import {
  AppWindow,
  ArrowLeftRight,
  Banknote,
  Boxes,
  Calculator,
  ChevronRight,
  Gift,
  Layers,
  Lock,
  Power,
  RefreshCw,
  Repeat,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { RevealSection } from "@/components/Reveal";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

export function SubsidizeLeaseSellPage() {
  const { messages, site, href } = useSite();
  const u = (path: string) => localizeUrl(path, site.locale, href);
  const salesCta = u("/contacts?inquiry=sales#inquiry-form");

  const heroStats = [
    { icon: Boxes, label: messages.bmStat1 },
    { icon: ArrowLeftRight, label: messages.bmStat2 },
    { icon: ShieldCheck, label: messages.bmStat3 },
  ];

  const models: {
    icon: LucideIcon;
    name: string;
    tagline: string;
    upfront: string;
    owns: string;
    revenue: string;
    best: string;
  }[] = [
    {
      icon: Gift,
      name: messages.bmModel1Name,
      tagline: messages.bmModel1Tagline,
      upfront: messages.bmModel1Upfront,
      owns: messages.bmModel1Owns,
      revenue: messages.bmModel1Revenue,
      best: messages.bmModel1Best,
    },
    {
      icon: Repeat,
      name: messages.bmModel2Name,
      tagline: messages.bmModel2Tagline,
      upfront: messages.bmModel2Upfront,
      owns: messages.bmModel2Owns,
      revenue: messages.bmModel2Revenue,
      best: messages.bmModel2Best,
    },
    {
      icon: Banknote,
      name: messages.bmModel3Name,
      tagline: messages.bmModel3Tagline,
      upfront: messages.bmModel3Upfront,
      owns: messages.bmModel3Owns,
      revenue: messages.bmModel3Revenue,
      best: messages.bmModel3Best,
    },
  ];

  const tableRows = [
    { label: messages.bmRow1Label, sub: messages.bmRow1Sub, lease: messages.bmRow1Lease, sell: messages.bmRow1Sell },
    { label: messages.bmRow2Label, sub: messages.bmRow2Sub, lease: messages.bmRow2Lease, sell: messages.bmRow2Sell },
    { label: messages.bmRow3Label, sub: messages.bmRow3Sub, lease: messages.bmRow3Lease, sell: messages.bmRow3Sell },
    { label: messages.bmRow4Label, sub: messages.bmRow4Sub, lease: messages.bmRow4Lease, sell: messages.bmRow4Sell },
    { label: messages.bmRow5Label, sub: messages.bmRow5Sub, lease: messages.bmRow5Lease, sell: messages.bmRow5Sell },
    { label: messages.bmRow6Label, sub: messages.bmRow6Sub, lease: messages.bmRow6Lease, sell: messages.bmRow6Sell },
    { label: messages.bmRow7Label, sub: messages.bmRow7Sub, lease: messages.bmRow7Lease, sell: messages.bmRow7Sell },
  ];

  const tcoCards = [
    { icon: Gift, title: messages.bmTco1Title, body: messages.bmTco1Body },
    { icon: Repeat, title: messages.bmTco2Title, body: messages.bmTco2Body },
    { icon: Banknote, title: messages.bmTco3Title, body: messages.bmTco3Body },
  ];

  const lockCards: { icon: LucideIcon; title: string; body: string }[] = [
    { icon: Lock, title: messages.bmLock1Title, body: messages.bmLock1Body },
    { icon: AppWindow, title: messages.bmLock2Title, body: messages.bmLock2Body },
    { icon: Power, title: messages.bmLock3Title, body: messages.bmLock3Body },
    { icon: RefreshCw, title: messages.bmLock4Title, body: messages.bmLock4Body },
  ];

  const mixItems: { icon: LucideIcon; text: string }[] = [
    { icon: Gift, text: messages.bmMix1 },
    { icon: Repeat, text: messages.bmMix2 },
    { icon: Banknote, text: messages.bmMix3 },
    { icon: RefreshCw, text: messages.bmMix4 },
  ];

  const faqs = [
    { q: messages.bmFaqQ1, a: messages.bmFaqA1 },
    { q: messages.bmFaqQ2, a: messages.bmFaqA2 },
    { q: messages.bmFaqQ3, a: messages.bmFaqA3 },
    { q: messages.bmFaqQ4, a: messages.bmFaqA4 },
    { q: messages.bmFaqQ5, a: messages.bmFaqA5 },
    { q: messages.bmFaqQ6, a: messages.bmFaqA6 },
  ];

  return (
    <SiteLayout>
      {/* Breadcrumb */}
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={u("/")} className="hover:text-foreground">
            {messages.guidesBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={u("/guides")} className="hover:text-foreground">
            {messages.navGuides}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.bmBreadcrumbPage}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div aria-hidden className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-40" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-0 -z-10 h-[440px] w-[600px] rounded-full opacity-40 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-24">
          <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            {messages.bmHeroEyebrow}
          </div>
          <h1
            className="mt-4 animate-fade-up text-balance text-4xl font-semibold tracking-tight sm:text-5xl"
            style={{ animationDelay: "80ms" }}
          >
            {messages.bmHeroTitle}{" "}
            <span className="text-gradient">{messages.bmHeroTitleAccent}</span>
          </h1>
          <p
            className="mx-auto mt-6 max-w-2xl animate-fade-up text-pretty text-lg text-muted-foreground"
            style={{ animationDelay: "160ms" }}
          >
            {messages.bmHeroSub}
          </p>
          <p
            className="mt-5 animate-fade-up text-pretty text-sm font-medium text-foreground/80"
            style={{ animationDelay: "220ms" }}
          >
            {messages.bmHeroNote}
          </p>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-3"
            style={{ animationDelay: "280ms" }}
          >
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.bmHeroCtaPrimary}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <a href="#control">{messages.bmHeroCtaSecondary}</a>
            </Button>
          </div>
          <div
            className="mt-8 flex animate-fade-up flex-wrap justify-center gap-2"
            style={{ animationDelay: "340ms" }}
          >
            {heroStats.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
              >
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Thesis — it's a financing decision */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.bmThesisEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.bmThesisTitle}
          </h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>{messages.bmThesisBody1}</p>
            <p>{messages.bmThesisBody2}</p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-[1fr_1.15fr]">
            <div className="rounded-2xl border border-primary/30 bg-card/70 p-5 shadow-glow">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Boxes className="h-4 w-4" />
                {messages.bmThesisCoreLabel}
              </div>
              <p className="mt-2 text-lg font-semibold text-foreground">
                {messages.bmThesisCoreValue}
              </p>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card/40 p-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Layers className="h-4 w-4" />
                {messages.bmThesisReachLabel}
              </div>
              <p className="mt-2 text-base font-medium text-foreground/90">
                {messages.bmThesisReachValue}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* The three models */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.bmModelsEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.bmModelsTitle}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-3">
            {models.map((m) => {
              const Icon = m.icon;
              return (
                <article
                  key={m.name}
                  className="flex flex-col rounded-2xl border border-border/60 bg-card/60 p-6"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="text-lg font-semibold text-foreground">{m.name}</h3>
                  </div>
                  <p className="mt-3 text-sm font-medium text-foreground/80">{m.tagline}</p>
                  <dl className="mt-5 space-y-3 border-t border-border/60 pt-4 text-sm">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {messages.bmModelLabelUpfront}
                      </dt>
                      <dd className="mt-0.5 text-foreground/90">{m.upfront}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {messages.bmModelLabelOwns}
                      </dt>
                      <dd className="mt-0.5 text-foreground/90">{m.owns}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {messages.bmModelLabelRevenue}
                      </dt>
                      <dd className="mt-0.5 text-foreground/90">{m.revenue}</dd>
                    </div>
                  </dl>
                  <div className="mt-4 rounded-xl bg-primary/[0.05] px-4 py-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {messages.bmModelLabelBest}
                    </span>
                    <p className="mt-1 text-sm text-foreground/90">{m.best}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </RevealSection>

      {/* Comparison table */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.bmTableEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.bmTableTitle}
            </h2>
          </div>
          <div className="mt-10 overflow-x-auto rounded-2xl border border-border/60 bg-card/60">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-muted-foreground">
                    {messages.bmTableColDimension || " "}
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <Gift className="h-4 w-4 text-primary" />
                      {messages.bmTableColSubsidize}
                    </span>
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <Repeat className="h-4 w-4 text-primary" />
                      {messages.bmTableColLease}
                    </span>
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 font-semibold text-foreground">
                      <Banknote className="h-4 w-4 text-primary" />
                      {messages.bmTableColSell}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => (
                  <tr key={r.label} className="border-b border-border/60 align-top last:border-0">
                    <th scope="row" className="px-4 py-3 text-left font-medium text-foreground/90">
                      {r.label}
                    </th>
                    <td className="px-4 py-3 text-muted-foreground">{r.sub}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.lease}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.sell}</td>
                  </tr>
                ))}
                <tr className="bg-primary/[0.06]">
                  <th scope="row" className="px-4 py-3 text-left font-semibold text-foreground">
                    {messages.bmTableSafeLabel}
                  </th>
                  <td colSpan={3} className="px-4 py-3 font-medium text-foreground/90">
                    <a
                      href={u("/solutions/alcatraz-dms")}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {messages.bmTableSafeValue}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">{messages.bmTableNote}</p>
        </div>
      </RevealSection>

      {/* TCO — cash-flow shapes */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.bmTcoEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.bmTcoTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {messages.bmTcoBody}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-3">
            {tcoCards.map(({ icon: Icon, title, body }) => (
              <article key={title} className="rounded-2xl border border-border/60 bg-card/60 p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="text-base font-semibold text-foreground">{title}</h3>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-center text-xs text-muted-foreground">
            {messages.bmTcoNote}
          </p>
        </div>
      </RevealSection>

      {/* What makes it safe — DMS locking */}
      <RevealSection id="control" className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.bmLockEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.bmLockTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {messages.bmLockBody}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {lockCards.map(({ icon: Icon, title, body }) => (
              <article key={title} className="rounded-2xl border border-border/60 bg-card/60 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
          <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-primary/20 bg-primary/[0.04] p-6 sm:flex-row sm:items-center">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <p className="text-sm leading-relaxed text-foreground/90">{messages.bmLockNote}</p>
          </div>
        </div>
      </RevealSection>

      {/* Most operators run more than one */}
      <RevealSection className="border-b border-border/60 section-alt py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.bmMixEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.bmMixTitle}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {messages.bmMixBody}
          </p>
          <ul className="mt-6 space-y-3">
            {mixItems.map(({ icon: Icon, text }) => (
              <li
                key={text}
                className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 px-4 py-3"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-sm text-foreground/90">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </RevealSection>

      {/* Where we fit */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">
            {messages.bmFitEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.bmFitTitle}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground">
            {messages.bmFitBody}
          </p>
          <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-primary/20 bg-primary/[0.04] p-6 sm:flex-row sm:items-center">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Calculator className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-base font-semibold text-foreground">{messages.bmFitNoteTitle}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {messages.bmFitNoteBody}
              </p>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* FAQ */}
      <RevealSection className="border-b border-border/60 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-primary">
              {messages.bmFaqEyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              {messages.bmFaqTitle}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map(({ q, a }, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-base font-medium">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </RevealSection>

      {/* Final CTA */}
      <RevealSection className="relative overflow-hidden bg-foreground py-16 text-background sm:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-0 opacity-30"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-background/70">
            {messages.bmCtaEyebrow}
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {messages.bmCtaTitle}
          </h2>
          <p className="mt-4 text-base text-background/80">{messages.bmCtaBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto">
              <a href={salesCta}>{messages.bmCtaPrimary}</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background sm:w-auto"
            >
              <a href={salesCta}>{messages.bmCtaSecondary}</a>
            </Button>
          </div>
        </div>
      </RevealSection>
    </SiteLayout>
  );
}
